import * as Sentry from "@sentry/react";
import { Menubar } from "primereact/menubar";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useClientId, useFeaturesForClientId } from "./ClientConfig";
import { IfFeatureEnabled } from "./IfFeatureEnabled";
import styles from "./LoggedIn.module.css";
import { PantheonAvatarWithMenu } from "./components/avatar/Avatar";
import { BrokerAnalytics } from "./components/crm-contact/BrokerAnalytics";
import { CRMContactsProvider } from "./components/crm-contact/CRMContactsProvider";
import { WhatsNew } from "./components/whats-new/WhatsNew";
import { Wordmark } from "./components/wordmark/Wordmark";
import { useNavigateWithReloadCheck } from "./context-providers/UpdateContext";
import { useIsMobile } from "./responsive/useMediaQuery";
import { Dashboard } from "./pages/specialty-assistant/dashboard/Dashboard";
import { useDefaultFilter } from "./pages/specialty-assistant/dashboard/filter-hooks";
import { useLocalUser } from "./pages/specialty-assistant/data";
import { BorModalProvider } from "./pages/specialty-assistant/bor";
import { MapModalProvider } from "./pages/specialty-assistant/map/MapModalContext";
import { ROUTES } from "./routes";
import { displayNameForUser } from "./util";

export const LoggedIn = () => {
  const loc = useLocation();
  const navigate = useNavigateWithReloadCheck();
  const clientId = useClientId();
  const features = useFeaturesForClientId(clientId, navigate);

  const me = useLocalUser();
  Sentry.setUser(me ? { id: me.id, email: me.email } : null);
  Sentry.setTag("client_id", me?.client_id);

  const defaultFilter = useDefaultFilter();
  const isMobile = useIsMobile();

  // Policy Fly dashboards aren't useful on phones — drop that top-nav menu.
  const menuModel = features
    .menuItems(me, loc.pathname)
    .filter((item) => !(isMobile && item.id === "policy_fly"));

  return (
    <BorModalProvider>
      <MapModalProvider>
        <>
          <WhatsNew />

          <div className={styles.loggedInContainer}>
            <Menubar
              style={{
                position: "relative",
                zIndex: 1000,
                borderRadius: "0px",
              }}
              start={
                <Wordmark
                  className={styles.wordMarkPadding}
                  height="32px"
                  onClick={() =>
                    navigate(
                      defaultFilter && features.isAsgardEnabled
                        ? ROUTES.DASHBOARD_WITH_FILTER(defaultFilter)
                        : features.defaultRoute(me)
                    )
                  }
                />
              }
              model={menuModel}
              className={styles.menubar}
              end={
                <IfFeatureEnabled feature="avatar-menu">
                  {me && (
                    <PantheonAvatarWithMenu
                      label={displayNameForUser(me)}
                      imageUrl={me.profile_image_url}
                      size="large"
                      withMenu
                    />
                  )}
                </IfFeatureEnabled>
              }
              pt={{
                menu: {
                  className: styles.wideSubmenu,
                },
              }}
            />
            <Routes>
              {features.isAsgardEnabled && (
                <Route
                  path={ROUTES.DASHBOARD + "/*"}
                  element={
                    <CRMContactsProvider>
                      <Dashboard clientId={clientId} />
                    </CRMContactsProvider>
                  }
                />
              )}
              {features.isBrokerAnalyticsEnabled && (
                <Route
                  path={ROUTES.BROKER_ANALYTICS}
                  element={<BrokerAnalytics />}
                />
              )}
              <Route
                path="*"
                element={<Navigate to={features.defaultRoute(me)} />}
              />
            </Routes>
          </div>
        </>
      </MapModalProvider>
    </BorModalProvider>
  );
};
